import { normalize } from 'normalizr'
import { Schemas } from 'redux-modules/schema'
import { updateEntities, deleteEntity } from 'redux-modules/entity'

import api from 'api'

const FETCH_REQUEST = 'realhub/publicationEdition/FETCH_REQUEST'
const FETCH_SUCCESS = 'realhub/publicationEdition/FETCH_SUCCESS'
const FETCH_FAILURE = 'realhub/publicationEdition/FETCH_FAILURE'
const CREATE_REQUEST = 'realhub/publicationEdition/CREATE_REQUEST'
const CREATE_SUCCESS = 'realhub/publicationEdition/CREATE_SUCCESS'
const CREATE_FAILURE = 'realhub/publicationEdition/CREATE_FAILURE'
const UPDATE_REQUEST = 'realhub/publicationEdition/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realhub/publicationEdition/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realhub/publicationEdition/UPDATE_FAILURE'
const DELETE_REQUEST = 'realhub/publicationEdition/DELETE_REQUEST'
const DELETE_SUCCESS = 'realhub/publicationEdition/DELETE_SUCCESS'
const DELETE_FAILURE = 'realhub/publicationEdition/DELETE_FAILURE'

// Initial State
const initialState = {
  creating: false,
  errors: [],
  loaded: false,
  loadedEditionsForPublicationIds: [],
  loading: false,
  result: [],
  updating: false,
}

// Actions
export function fetchRequest(payload = {}){
  return {
    type: FETCH_REQUEST,
    loadedEditionsForPublicationIds: payload.loadedEditionsForPublicationIds,
  }
}

export function fetchSuccess(payload = {}){
  return {
    type: FETCH_SUCCESS,
    result: payload.result,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(){
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(){
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(payload = {}){
  return {
    type: DELETE_SUCCESS,
    result: payload.result,
  }
}

export function deleteFailure(errors = []){
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Helper Functions
const handleError = errors => errors || ['Something went wrong']

// Action Creators
export function loadPublicationEditions(publicationId, options){
  return (dispatch, getState) => {
    const loadedEditionsForPublicationIds = getState().users.loadedEditionsForPublicationIds
      ? getState().users.loadedEditionsForPublicationIds.slice()
      : []

    if (!loadedEditionsForPublicationIds.includes(publicationId)){
      loadedEditionsForPublicationIds.push(publicationId)
    }

    // Set Loading
    dispatch(fetchRequest({ loadedEditionsForPublicationIds }))

    const promise = api(`/admin/publications/${publicationId}/editions.json`, options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.PUBLICATION_EDITION_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess(normalizedJson))
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function createPublicationEdition(edition, options){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      publication_edition: edition,
    }),
  }

  return (dispatch, getState) => {
    // Set Loading
    dispatch(createRequest())

    const promise = api(`/admin/publications/${edition.publication_id}/editions.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        // Add to result array
        const resultArray = getState().publicationProducts.result ? getState().publicationProducts.result.slice() : []
        if (!resultArray.includes(json.id)){
          resultArray.push(json.id)
        }
        const normalizedJson = normalize(json, Schemas.PUBLICATION_EDITION)
        const publications = { ...getState().entities.publications }
        if (publications[edition.publication_id]){
          const publication = publications[edition.publication_id]
          publication.editions.push(json.id)
          dispatch(updateEntities(normalize(publication, Schemas.PUBLICATION)))
        }
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess({ result: resultArray }))
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))
      })

    return promise
  }
}

export function updatePublicationEdition(params, options){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      publication_edition: params,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(updateRequest())

    const promise = api(`/admin/publications/${params.publication_id}/editions/${params.id}.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }
        const normalizedJson = normalize(json, Schemas.PUBLICATION_EDITION)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
      })

    return promise
  }
}

export function deletePublicationEdition(publicationEdition, options){
  const config = {
    method: 'DELETE',
  }

  return (dispatch) => {
    // Set Loading
    dispatch(deleteRequest())

    const promise = api(
      `/admin/publications/${publicationEdition.publication_id}/editions/${publicationEdition.id}.json`,
      options,
      config,
    )
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }
        const normalizedJson = normalize(json, Schemas.PUBLICATION_EDITION)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(deleteFailure(errors))
      })

    return promise
  }
}

export function generatePublicationEditions(publicationId, options){
  const config = {
    method: 'POST',
  }

  return (dispatch) => {
    // Set Loading
    dispatch(createRequest())

    const promise = api(`/admin/publications/${publicationId}/editions/auto_generate.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }
        const normalizedJson = normalize(json, Schemas.PUBLICATION_EDITION_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))
      })

    return promise
  }
}

export function rebuildEditionDeadlines(publicationId, options, callback){
  const config = {
    method: 'PUT',
  }

  return (dispatch) => {
    const promise = api(`/admin/publications/${publicationId}/editions/set_deadlines.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.PUBLICATION_EDITION_ARRAY)
        dispatch(updateEntities(normalizedJson))

        if (callback) callback(json)
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: action.loading,
        loadedEditionsForPublicationIds:
          action.loadedEditionsForPublicationIds || state.loadedEditionsForPublicationIds,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: action.loading,
        loaded: action.loaded,
        result: action.result,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: action.loading,
        loaded: action.loaded,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return { ...state, creating: true }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        result: action.result,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return { ...state, updating: true }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors,
      }
    case DELETE_REQUEST:
      return { ...state, deleting: true }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        result: action.result,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors,
      }
    default:
      return state
  }
}
